import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, Form, Input, Modal, Row, Space, Switch, Table, Tag, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;

const PERMISSION_GROUPS = [
  { title: 'Dashboard', items: [{ key: 'dashboard.read', label: 'View dashboard' }] },
  {
    title: 'Orders',
    items: [
      { key: 'orders.read', label: 'View orders' },
      { key: 'orders.create', label: 'Create orders' },
      { key: 'orders.update', label: 'Update orders' },
      { key: 'orders.assign', label: 'Assign driver / shipment actions' },
      { key: 'orders.payment', label: 'Update payment type' },
      { key: 'orders.archive', label: 'Archive / unarchive orders' },
      { key: 'orders.delete', label: 'Delete orders' },
    ]
  },
  {
    title: 'Drivers',
    items: [
      { key: 'drivers.read', label: 'View drivers' },
      { key: 'drivers.create', label: 'Create drivers' },
      { key: 'drivers.update', label: 'Update drivers' },
      { key: 'drivers.delete', label: 'Delete drivers' },
    ]
  },
  {
    title: 'Warehouse',
    items: [
      { key: 'warehouse.read', label: 'View warehouses' },
      { key: 'warehouse.create', label: 'Create warehouses' },
      { key: 'warehouse.update', label: 'Update warehouses' },
      { key: 'warehouse.delete', label: 'Delete warehouses' },
    ]
  },
  { title: 'Shipments', items: [{ key: 'shipments.read', label: 'View shipments' }, { key: 'shipments.write', label: 'Manage shipments' }] },
  { title: 'Live Tracking', items: [{ key: 'live_tracking.read', label: 'View live tracking' }] },
  {
    title: 'Notifications',
    items: [
      { key: 'notifications.read', label: 'View notifications' },
      { key: 'notifications.write', label: 'Send notifications' },
      { key: 'notifications.delete', label: 'Delete notifications' },
    ]
  },
  { title: 'Settings', items: [{ key: 'settings.read', label: 'View settings' }, { key: 'settings.write', label: 'Manage settings' }] },
  { title: 'API Docs', items: [{ key: 'api_docs.read', label: 'View API docs' }] },
];

export default function Staff() {
  const userDetail = useSelector((s) => s.UserData.userData);
  const isStoreOwner = userDetail?.user?.role === 'store_owner' || userDetail?.role === 'user';
  const isSuperAdmin = userDetail?.role === 'superAdmin';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await ApiServices('get', ApiConfig.STAFF);
      if (res?.success) setData(res.data || []);
      else errorMsg(res?.message || 'Failed to load staff');
    } catch (e) {
      errorMsg(e?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      status: true,
      permissions: {},
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      firstname: row.firstname,
      lastname: row.lastname,
      email: row.email,
      phoneNumber: row.phoneNumber,
      status: !!row.status,
      permissions: row.permissions || {},
      password: '',
    });
    setOpen(true);
  };

  const columns = useMemo(() => ([
    { title: 'Name', dataIndex: 'firstname', key: 'name', render: (_, r) => <Text strong>{`${r.firstname || ''} ${r.lastname || ''}`.trim() || '—'}</Text> },
    { title: 'Email', dataIndex: 'email', key: 'email', responsive: ['md'] },
    { title: 'Phone', dataIndex: 'phoneNumber', key: 'phoneNumber', responsive: ['lg'] },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => (Number(v) === 1 || v === true ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>),
      width: 110,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)} icon={<SettingOutlined />}>Edit</Button>
        </Space>
      ),
      width: 120,
    },
  ]), []);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phoneNumber: values.phoneNumber,
        status: values.status ? 1 : 0,
        permissions: values.permissions || {},
      };
      if (values.password) payload.password = values.password;

      const res = editing
        ? await ApiServices('put', `${ApiConfig.STAFF}/${editing.id}`, payload)
        : await ApiServices('post', ApiConfig.STAFF, { ...payload, password: values.password });

      if (res?.success) {
        successMsg(editing ? 'Staff updated' : 'Staff created');
        setOpen(false);
        fetchStaff();
      } else {
        errorMsg(res?.message || 'Failed');
      }
    } catch (e) {
      // validation errors are handled by antd
      if (e?.message) errorMsg(e.message);
    }
  };

  if (!isStoreOwner && !isSuperAdmin) {
    return (
      <MainLayout>
        <Card>
          <Text>You don’t have access to Staff management.</Text>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <Space size={10}>
            <TeamOutlined style={{ color: '#1677ff', fontSize: 20 }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>Staff</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>Create staff users and assign module permissions.</Text>
            </div>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchStaff} loading={loading}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Staff</Button>
          </Space>
        </div>

        <Card style={{ borderRadius: 14 }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
          />
        </Card>

        <Modal
          title={editing ? 'Edit Staff' : 'Add Staff'}
          open={open}
          onCancel={() => setOpen(false)}
          onOk={submit}
          okText={editing ? 'Update' : 'Create'}
          width={900}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item name="firstname" label="First name" rules={[{ required: true }]}>
                  <Input placeholder="First name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="lastname" label="Last name" rules={[{ required: true }]}>
                  <Input placeholder="Last name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="phoneNumber" label="Phone">
                  <Input placeholder="Phone number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                  <Input placeholder="Email" disabled={!!editing} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="password"
                  label={editing ? 'Password (optional)' : 'Password'}
                  rules={editing ? [] : [{ required: true }]}
                >
                  <Input.Password placeholder={editing ? 'Leave empty to keep current' : 'Password'} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="status" label="Active" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginTop: 8, marginBottom: 6, fontWeight: 700, color: '#1e293b' }}>Permissions</div>
            <Row gutter={[12, 12]}>
              {PERMISSION_GROUPS.map((g) => (
                <Col key={g.title} xs={24} md={12}>
                  <Card size="small" style={{ borderRadius: 12, background: '#fafafa' }} title={g.title}>
                    {g.items.map((it) => (
                      <Form.Item key={it.key} name={['permissions', it.key]} valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox>{it.label}</Checkbox>
                      </Form.Item>
                    ))}
                  </Card>
                </Col>
              ))}
            </Row>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
}

